import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Row, Col, Badge, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPercent, 
  faDollarSign, 
  faUsers, 
  faCrown, 
  faStar, 
  faUser, 
  faUserCheck,
  faCalendarAlt,
  faTag,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import adminFeeService from '../../services/adminFeeService';

const AdminFeeModal = ({ 
  show, 
  onHide, 
  onSelectFee, 
  clientId, 
  orderStartDate, 
  orderEndDate, 
  grossInvestment 
}) => {
  const [availableFees, setAvailableFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFee, setSelectedFee] = useState(null);

  // Cargar fees válidos cuando se abre el modal
  useEffect(() => {
    if (show && clientId && orderStartDate && orderEndDate) {
      loadValidFees();
    }
  }, [show, clientId, orderStartDate, orderEndDate, grossInvestment]);

  const loadValidFees = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const fees = await adminFeeService.getValidFeesForOrder(
        clientId,
        new Date(orderStartDate),
        new Date(orderEndDate),
        grossInvestment
      );
      

      
      setAvailableFees(fees);
    } catch (err) {
      console.error('Error loading valid fees:', err);
      setError('Error al cargar las configuraciones de fee disponibles');
    } finally {
      setLoading(false);
    }
  };

  const getAppliesIcon = (appliesTo) => {
    const icons = {
      'ALL': faUsers,
      'VIP_CLIENTS': faCrown,
      'PREMIUM_CLIENTS': faStar,
      'SMALL_CLIENTS': faUser,
      'SPECIFIC_CLIENTS': faUserCheck
    };
    return icons[appliesTo] || faUser;
  };

  const formatDateRange = (validFrom, validTo) => {
    const from = new Date(validFrom).toLocaleDateString('es-ES');
    const to = new Date(validTo).toLocaleDateString('es-ES');
    return `${from} - ${to}`;
  };

  const calculateFeeAmount = (fee) => {
    if (!grossInvestment) return null;
    return adminFeeService.calculateFee(fee, grossInvestment);
  };

  const handleSelectFee = () => {
    if (selectedFee) {
      const feeAmount = calculateFeeAmount(selectedFee);
      onSelectFee({
        ...selectedFee,
        calculatedAmount: feeAmount
      });
      onHide();
      setSelectedFee(null);
    }
  };

  const handleClose = () => {
    setSelectedFee(null);
    onHide();
  };



  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faTag} className="me-2" />
          Seleccionar Configuración de Fee Admin
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Cargando configuraciones disponibles...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            {error}
          </Alert>
        )}

        {!loading && !error && availableFees.length === 0 && (
          <Alert variant="warning">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            No hay configuraciones de Fee Admin disponibles para esta orden.
            <br />
            <small>
              Verifique las fechas de la orden y el cliente seleccionado.
            </small>
          </Alert>
        )}

        {!loading && !error && availableFees.length > 0 && (
          <>
            <Alert variant="info" className="mb-3">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              Se encontraron <strong>{availableFees.length}</strong> configuraciones válidas para esta orden.
            </Alert>

            <Row>
              {availableFees.map((fee) => {
                const feeAmount = calculateFeeAmount(fee);
                const isSelected = selectedFee?.id === fee.id;
                
                return (
                  <Col md={6} key={fee.id} className="mb-3">
                    <Card 
                      className={`h-100 ${isSelected ? 'border-primary shadow' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedFee(fee)}
                    >
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="card-title mb-0">{fee.name}</h6>
                          <Badge 
                            bg={fee.feeType === 'percentage' ? 'success' : 'warning'}
                            className="ms-2"
                          >
                            <FontAwesomeIcon 
                              icon={fee.feeType === 'percentage' ? faPercent : faDollarSign} 
                              className="me-1" 
                            />
                            {fee.feeType === 'percentage' 
                              ? `${fee.feeValue}%` 
                              : `$${fee.feeValue.toLocaleString()}`
                            }
                          </Badge>
                        </div>

                        {fee.description && (
                          <p className="text-muted small mb-2">{fee.description}</p>
                        )}

                        <div className="d-flex align-items-center mb-2">
                          <FontAwesomeIcon 
                            icon={getAppliesIcon(fee.appliesTo)} 
                            className="me-2 text-muted" 
                          />
                          <small className="text-muted">
                            {adminFeeService.formatAppliesTo(fee.appliesTo)}
                          </small>
                        </div>

                        <div className="d-flex align-items-center mb-2">
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-muted" />
                          <small className="text-muted">
                            {formatDateRange(fee.validFrom, fee.validTo)}
                          </small>
                        </div>

                        {grossInvestment && feeAmount && (
                          <div className="mt-3 p-2 bg-light rounded">
                            <div className="d-flex justify-content-between">
                              <small><strong>Fee Calculado:</strong></small>
                              <small className="text-success">
                                <strong>${feeAmount.toLocaleString()}</strong>
                              </small>
                            </div>
                          </div>
                        )}

                        {isSelected && (
                          <div className="mt-2">
                            <Badge bg="primary" className="w-100">
                              ✓ Seleccionado
                            </Badge>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {selectedFee && grossInvestment && (
              <small className="text-muted">
                Fee seleccionado: <strong>${calculateFeeAmount(selectedFee)?.toLocaleString()}</strong>
              </small>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={handleClose} className="me-2">
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSelectFee}
              disabled={!selectedFee}
            >
              Aplicar Fee Seleccionado
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AdminFeeModal; 